import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Field, Badge } from "@/components/ui-hud";
import { api } from "@/lib/api";
import { redirectExternal } from "@/lib/redirect";
import { toast } from "sonner";
import { Zap, X, AlertTriangle, ArrowRight, ArrowLeft, Loader2, CheckCircle2, Rocket, DollarSign, CreditCard, Globe2 } from "lucide-react";
import AirportInput from "@/components/AirportInput";
import RoutingInput from "@/components/RoutingInput";

const STEPS = ["Your Contact Info", "Trip Route", "Aircraft & Role", "Timing & Pay", "Notes", "Review & Send"];

export default function HireCrewNow() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    contact_name: "", contact_email: "", contact_phone: "",
    departure_icao: "", destination_icao: "",
    routing: [],
    aircraft_type: "", role_needed: "PIC",
    trip_start: "", trip_end: "", pay_rate: "",
    notes: "", urgency: "critical",
    asap: false, bonus_1hr_arrival: false, bonus_amount: 250,
    est_trip_days: 1,
  });

  const [catalog, setCatalog] = useState({});
  const [rate, setRate] = useState(null);
  useEffect(() => {
    api.get("/faa/type-ratings/catalog").then((r) => setCatalog(r.data || {})).catch(() => {});
  }, []);

  // Fetch suggested rate whenever aircraft type changes
  useEffect(() => {
    if (!form.aircraft_type) { setRate(null); return; }
    api.get(`/faa/suggested-rate/${encodeURIComponent(form.aircraft_type)}`)
      .then((r) => setRate(r.data))
      .catch(() => setRate(null));
  }, [form.aircraft_type]);

  const startedAt = useRef(Date.now());
  const [elapsed, setElapsed] = useState("00:00:00");
  useEffect(() => {
    const id = setInterval(() => {
      const s = Math.floor((Date.now() - startedAt.current) / 1000);
      const hh = String(Math.floor(s / 3600)).padStart(2, "0");
      const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
      const ss = String(s % 60).padStart(2, "0");
      setElapsed(`${hh}:${mm}:${ss}`);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const next = () => {
    if (step === 0 && (!form.contact_name || !form.contact_email)) return toast.error("Name and email required");
    if (step === 1 && (!form.departure_icao || !form.destination_icao)) return toast.error("Both airports required");
    if (step === 2 && (!form.aircraft_type || !form.role_needed)) return toast.error("Aircraft type & role required");
    if (step === 3 && !form.trip_start && !form.asap) return toast.error("Trip start required — or press ASAP to skip");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const goAsap = () => {
    setForm((f) => ({ ...f, asap: true, trip_start: "", trip_end: "" }));
    // Jump straight to Notes (skip past Timing since it's now moot)
    setStep(4);
    toast.success("ASAP dispatch selected — timing skipped");
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/hire-crew-now", {
        ...form,
        departure_icao: form.departure_icao.toUpperCase(),
        destination_icao: form.destination_icao.toUpperCase(),
        routing: (form.routing || []).map((x) => x.toUpperCase()),
      });
      setResult(r.data);
      toast.success(`Alert sent · ${r.data.matched_count} pilots notified`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send alert");
    } finally { setBusy(false); }
  };

  const guaranteeWithCard = async () => {
    if (!result?.hire_id) return;
    setBusy(true);
    try {
      const r = await api.post("/hire-crew-now/setup-card", {
        hire_id: result.hire_id,
        origin_url: window.location.origin,
      });
      if (!redirectExternal(r.data.setup_url)) {
        setBusy(false);
        toast.error("Popup blocked. Please allow pop-ups and try again.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open card capture");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] bg-black/60 py-10 px-4 flex items-start md:items-center justify-center">
      <Card testId="hire-crew-now-modal" className="w-full max-w-2xl relative" style={{border: "1px solid rgba(235,87,87,0.5)", boxShadow: "0 0 42px rgba(235,87,87,0.20)"}}>
        <button
          data-testid="btn-hire-close"
          onClick={() => nav(-1)}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/5 text-white/50 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md mono text-[11px] font-bold tracking-wider" style={{background:"rgba(235,87,87,0.14)", color:"#EB5757", border:"1px solid rgba(235,87,87,0.5)"}}>
              <Zap className="h-3 w-3" /> URGENT
            </div>
            <h2 className="font-display text-2xl font-bold tracking-tight">Hire Crew Now</h2>
          </div>
          <div className="hidden md:block bg-black/60 px-3 py-1.5 rounded-md border border-red-500/40">
            <div className="mono text-red-400 text-lg font-bold tracking-widest" data-testid="hire-elapsed">{elapsed}</div>
            <div className="mono text-[9px] tracking-widest text-red-400/60 text-center">ELAPSED</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 mb-1">
          <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full bg-red-500 transition-all"
              style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            />
          </div>
        </div>
        <div className="mono text-[11px] text-white/50 mb-5">
          Step {step + 1} of {STEPS.length} — <span className="text-white/80">{STEPS[step]}</span>
        </div>

        {/* URGENT banner */}
        {step < STEPS.length - 1 && (
          <div className="mb-5 rounded-lg border border-red-500/40 bg-red-500/[0.06] p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="mono text-sm font-bold text-red-400 tracking-wider">I NEED CREW NOW — URGENT</div>
              <div className="text-xs text-white/70 mt-1">Triggers immediate push alert to all available matching crew.</div>
            </div>
            <span className="h-2.5 w-2.5 bg-red-500 rounded-full animate-pulse mt-1.5" />
          </div>
        )}

        {result ? (
          <SentConfirmation
            result={result}
            form={form}
            busy={busy}
            onGuarantee={guaranteeWithCard}
            onDone={() => nav("/dashboard")}
          />
        ) : (
          <>
            {step === 0 && (
              <div className="space-y-4">
                <Field label="Your Name *">
                  <input data-testid="hire-name" required className="input" value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} placeholder="Full name" />
                </Field>
                <Field label="Email *">
                  <input data-testid="hire-email" required type="email" className="input" value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} placeholder="you@example.com" />
                </Field>
                <Field label="Phone">
                  <input data-testid="hire-phone" className="input mono" value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} placeholder="+1 (555) 000-0000" />
                </Field>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Departure Airport *">
                    <AirportInput
                      testId="hire-departure"
                      value={form.departure_icao}
                      onChange={(icao) => setForm({...form, departure_icao: icao})}
                      placeholder="Type city or ICAO (KTEB, Miami…)"
                    />
                  </Field>
                  <Field label="Destination Airport *">
                    <AirportInput
                      testId="hire-destination"
                      value={form.destination_icao}
                      onChange={(icao) => setForm({...form, destination_icao: icao})}
                      placeholder="Type city or ICAO (KOPF, London…)"
                    />
                  </Field>
                </div>

                <Field label={<span>Full Route — all legs <span className="text-white/40 text-xs">(optional but recommended for multi-leg / intl)</span></span>}>
                  <RoutingInput
                    testId="hire-routing"
                    value={form.routing}
                    onChange={(routing) => setForm({...form, routing})}
                  />
                </Field>

                <div className="mono text-xs text-white/50">
                  Paste something like <span className="text-cyan">KOPF-TNCM-KOPF-KTEB-EGLL-LFMN-KOPF</span> — we'll flag it as INTL and warn the crew to bring their passport.
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <Field label="Aircraft Type *">
                  <select data-testid="hire-aircraft" required className="input" value={form.aircraft_type} onChange={(e) => setForm({...form, aircraft_type: e.target.value})}>
                    <option value="">Select aircraft…</option>
                    {Object.entries(catalog)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([oem, list]) => (
                        <optgroup key={oem} label={oem}>
                          {[...list]
                            .sort((a, b) => a.label.localeCompare(b.label))
                            .map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                        </optgroup>
                      ))}
                  </select>
                </Field>
                <Field label="Role Needed *">
                  <div className="grid grid-cols-3 gap-2">
                    {["PIC", "SIC", "PIC+SIC"].map((r) => (
                      <button
                        key={r}
                        type="button"
                        data-testid={`hire-role-${r}`}
                        onClick={() => setForm({...form, role_needed: r})}
                        className={`px-3 py-3 rounded-lg mono text-sm font-bold border transition-all ${form.role_needed === r ? "bg-cyan/20 border-cyan text-cyan" : "border-white/10 text-white/70 hover:border-white/30"}`}
                      >{r}</button>
                    ))}
                  </div>
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                {/* Prominent ASAP button */}
                <button
                  type="button"
                  data-testid="btn-hire-asap"
                  onClick={goAsap}
                  className="w-full rounded-xl p-4 border-2 border-red-500 bg-red-500/[0.08] hover:bg-red-500/[0.15] transition-all group text-left flex items-center gap-4"
                >
                  <div className="h-11 w-11 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 animate-pulse">
                    <Rocket className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="mono text-red-400 text-sm font-bold tracking-wider">ASAP · DISPATCH NOW</div>
                    <div className="text-xs text-white/70 mt-0.5">Aircraft is fueled and waiting — skip calendar, first pilot in wins.</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-red-400 group-hover:translate-x-1 transition-transform" />
                </button>

                <div className="text-center text-[11px] mono text-white/40 tracking-widest">— OR SCHEDULE —</div>

                <Field label="Trip Start (date & time)">
                  <input data-testid="hire-start" type="datetime-local" className="input" value={form.trip_start} onChange={(e) => setForm({...form, trip_start: e.target.value, asap: false})} />
                </Field>
                <Field label="Trip End (optional)">
                  <input data-testid="hire-end" type="datetime-local" className="input" value={form.trip_end} onChange={(e) => setForm({...form, trip_end: e.target.value})} />
                </Field>

                {/* Suggested rate + Pay rate + Bonus checkbox */}
                <div className="rounded-lg border border-cyan/25 bg-cyan/[0.04] p-4 space-y-3">
                  {rate?.day_rate ? (
                    <div className="flex items-start gap-3">
                      <DollarSign className="h-4 w-4 text-cyan mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <div className="mono text-cyan font-bold tracking-wider text-[13px]">
                          SUGGESTED · ${rate.day_rate.toLocaleString()}/day + ${rate.per_diem}/day per diem
                        </div>
                        <div className="text-xs text-white/60 mt-0.5">
                          2026 market rate for {rate.class || "this aircraft"} · contract pilots
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-white/50 mono">No suggested rate on file for this type — enter your rate.</div>
                  )}

                  <Field label="Pay Rate (offer)">
                    <input
                      data-testid="hire-pay"
                      className="input"
                      value={form.pay_rate}
                      onChange={(e) => setForm({...form, pay_rate: e.target.value})}
                      placeholder={rate?.day_rate ? `$${rate.day_rate}/day + $${rate.per_diem} per diem` : "$1,200/day + $150 per diem"}
                    />
                  </Field>

                  <label
                    data-testid="hire-bonus"
                    className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer ${form.bonus_1hr_arrival ? "border-emerald-400 bg-emerald-400/[0.08]" : "border-white/10 hover:border-white/30 bg-black/20"}`}
                  >
                    <input
                      type="checkbox"
                      data-testid="hire-bonus-check"
                      className="h-4 w-4 mt-0.5 flex-shrink-0"
                      checked={form.bonus_1hr_arrival}
                      onChange={(e) => setForm({...form, bonus_1hr_arrival: e.target.checked})}
                    />
                    <div className="flex-1">
                      <div className="mono text-emerald-400 font-bold text-[13px] tracking-wider">
                        + $250 BONUS · PILOT AT PLANE WITHIN 1 HOUR
                      </div>
                      <div className="text-xs text-white/60 mt-1">
                        Held in your PJP account, paid out to the pilot's PJP wallet at end of trip.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {step === 4 && (
              <Field label="Notes for the Crew">
                <textarea data-testid="hire-notes" rows={5} className="input" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Pop-up trip, cancellation coverage, specific catering, currency requirements, etc." />
              </Field>
            )}

            {step === 5 && (
              <div className="space-y-2 mono text-sm">
                <ReviewRow k="From" v={`${form.contact_name} · ${form.contact_email}${form.contact_phone ? ` · ${form.contact_phone}`:""}`} />
                {form.routing?.length > 0 ? (
                  <ReviewRow k="Route" v={form.routing.join(" → ")} />
                ) : (
                  <ReviewRow k="Route" v={`${form.departure_icao} → ${form.destination_icao}`} />
                )}
                <ReviewRow k="Aircraft" v={form.aircraft_type} />
                <ReviewRow k="Role" v={form.role_needed} />
                {form.asap ? (
                  <ReviewRow k="Start" v={<span className="mono text-red-400 font-bold">ASAP · DISPATCH NOW</span>} />
                ) : (
                  <>
                    <ReviewRow k="Start" v={form.trip_start} />
                    {form.trip_end && <ReviewRow k="End" v={form.trip_end} />}
                  </>
                )}
                {form.pay_rate && <ReviewRow k="Pay" v={form.pay_rate} />}
                {form.bonus_1hr_arrival && (
                  <ReviewRow k="Bonus" v={<span className="mono text-emerald-400 font-bold">+ ${form.bonus_amount} · Pilot at plane within 1 hr</span>} />
                )}
                {form.notes && <ReviewRow k="Notes" v={form.notes} />}
              </div>
            )}

            {/* Nav buttons */}
            <div className="flex items-center justify-between mt-8 pt-5 border-t border-white/[0.06]">
              {step > 0 ? (
                <button data-testid="btn-hire-back" onClick={back} className="btn"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
              ) : (
                <button data-testid="btn-hire-cancel" onClick={() => nav(-1)} className="btn">Cancel</button>
              )}
              {step < STEPS.length - 1 ? (
                <button data-testid="btn-hire-next" onClick={next} className="btn btn-primary">Next <ArrowRight className="h-3.5 w-3.5" /></button>
              ) : (
                <button data-testid="btn-hire-submit" onClick={submit} disabled={busy} className="btn btn-primary" style={{background:"#EB5757", borderColor:"#EB5757"}}>
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>SEND URGENT ALERT <ArrowRight className="h-3.5 w-3.5" /></>}
                </button>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function ReviewRow({ k, v }) {
  return (
    <div className="flex gap-4 py-2 border-b border-white/[0.05] last:border-b-0">
      <div className="w-24 eyebrow-muted uppercase">{k}</div>
      <div className="flex-1 text-white/90">{v}</div>
    </div>
  );
}

function SentConfirmation({ result, form, busy, onGuarantee, onDone }) {
  const totalUsd = result.est_total_usd || 0;
  return (
    <div className="text-center py-2" data-testid="hire-sent">
      <CheckCircle2 className="h-14 w-14 text-emerald-400 mx-auto mb-3" />
      <div className="overline text-emerald-400 mb-1">ALERT DISPATCHED</div>
      <h3 className="font-display text-2xl font-bold tracking-tight mb-2">
        {result.matched_count} matching pilot{result.matched_count === 1 ? "" : "s"} notified.
      </h3>
      {result.is_international && (
        <div className="inline-flex items-center gap-2 mono text-[11px] text-orange-hud bg-orange-hud/[0.10] border border-orange-hud/40 px-3 py-1 rounded-full mb-3">
          <Globe2 className="h-3 w-3" /> INTL · BRING PASSPORT
        </div>
      )}
      <p className="text-sm text-white/60 max-w-md mx-auto mb-6">
        Broadcast to every FAA-verified {form.aircraft_type} pilot. Responses land in your Inbox — a copy also goes to <span className="mono text-cyan">{form.contact_email}</span>.
      </p>

      {totalUsd > 0 && (
        <div className="rounded-xl border-2 border-cyan/40 bg-cyan/[0.05] p-5 max-w-md mx-auto mb-4 text-left">
          <div className="overline text-cyan mb-3">// GUARANTEE THIS TRIP</div>
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-sm text-white/60">Estimated total</span>
            <span className="mono text-3xl font-bold text-cyan">${totalUsd.toLocaleString()}</span>
          </div>
          <div className="text-[11px] text-white/45 mb-4 mono">
            Day rate × {result.est_trip_days || 1} days + per diem{form.bonus_1hr_arrival ? ` + $${form.bonus_amount} bonus` : ""} + 3.5% PJP fee
          </div>
          <button
            data-testid="btn-guarantee-card"
            onClick={onGuarantee}
            disabled={busy}
            className="btn btn-primary w-full justify-center h-11"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4" /> SAVE CARD & GUARANTEE TRIP <ArrowRight className="h-3.5 w-3.5" /></>}
          </button>
          <div className="text-[11px] text-white/50 text-center mt-3 mono">
            Card saved via Stripe — no charge now. Captured at trip completion, paid to crew's PJP wallet.
          </div>
        </div>
      )}

      <button data-testid="btn-hire-skip-card" onClick={onDone} className="text-xs text-white/40 hover:text-white/70 underline underline-offset-2 mono">
        Skip guarantee for now — dispatch without card on file
      </button>
    </div>
  );
}
